import { FormatInlineBlocks } from '../../internal/Helpers';
import { Localized } from '../../internal/Localized';
import { HexStatCluster, HexStatRotator, ReadHeroStats } from './HexStat';
import Styles from './Landing.module.css';
import { LandingConstants } from './LandingConstants';
import { Button, Label } from '@app/engine';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { INicheContentDTO } from '@shared/common';

/** Props for the HeroCard. */
interface IHeroCardProps {
  niches: INicheContentDTO[];
  onCalculate: () => void;
}

/** Props for the EyebrowRotator. */
interface IEyebrowRotatorProps {
  words: string[];
  title?: string;
}

/**
 * Vertical word rotator used in the hero eyebrow line.
 *
 * @param props - Component props.
 * @returns The animated rotator element.
 */
function EyebrowRotator(props: IEyebrowRotatorProps) {
  // Working order of the words. The visible word is always the first entry; advancing rotates the front word to the back so the conveyor never has to wind back to the start.
  const [order, setOrder] = useState<string[]>(props.words);
  // True while the inner column is sliding up by one line; reset to false (with the transition disabled) once the front word has been rotated to the back so the snap is instant.
  const [shifting, setShifting] = useState(false);

  // Re-seed when the source words change (language swap or niche load).
  useEffect(() => setOrder(props.words), [props.words]);

  // Advance one word per interval by sliding the column up a single line.
  useEffect(() => {
    if (order.length <= 1) return;
    const id = window.setInterval(() => setShifting(true), LandingConstants.eyebrowIntervalMs);
    return () => window.clearInterval(id);
  }, [order.length]);

  // Once the slide completes, rotate the top word to the end and clear shifting in the same render. With shifting false the transition is off, so the offset snaps back to zero instantly.
  const handleTransitionEnd = useCallback(() => {
    setOrder((prev) => [...prev.slice(1), prev[0]]);
    setShifting(false);
  }, []);

  // Drive transform AND transition from state together so React commits both atomically. While shifting we animate up a line; otherwise transition is off, so the reset never animates back.
  const innerStyle = shifting
    ? { transform: `translateY(-${LandingConstants.eyebrowLineEm}em)`, transition: `transform ${LandingConstants.eyebrowAnimMs}ms cubic-bezier(0.4, 0, 0.2, 1)` }
    : { transform: 'translateY(0)', transition: 'none' };

  return (
    // Raw <span>: structural wrapper grouping the rotator column; engine Label renders text only, not child elements.
    <span className={Styles.eyebrowRotator}>
      {/* Raw <span>: the sliding column needs an inline transform style and an onTransitionEnd handler, which no engine control supports. */}
      <span className={Styles.eyebrowInner} style={innerStyle} onTransitionEnd={handleTransitionEnd}>
        {order.map((word, position) => (
          <Label key={`${word}-${position}`} text={word} title={props.title} classNameContainer={Styles.eyebrowWord} />
        ))}
      </span>
    </span>
  );
}

/** Props for the Ticker. */
interface ITickerProps {
  items: string[];
}

/**
 * Auto-scrolling industry ticker strip pinned to the hero footer.
 *
 * @param props - Component props.
 * @returns The ticker element.
 */
function Ticker(props: ITickerProps) {
  const items = props.items;
  const viewportRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  // How many times the base item list repeats inside ONE marquee group. Measured up so a single group
  // always spans at least the viewport, which is what makes the two-group `-50%` slide loop seamlessly.
  const [repeats, setRepeats] = useState<number>(LandingConstants.tickerMinGroupRepeats);

  // After layout, grow the repeat count until a single group overflows the viewport. The base width is
  // the current group width divided by the current repeat count; the needed count fills the viewport
  // plus one spare copy. Re-runs (via `repeats` in deps) until it converges, guarded so it cannot loop.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const group = groupRef.current;
    if (!viewport || !group) return;
    const baseWidth = group.scrollWidth / repeats;
    if (baseWidth <= 0) return;
    const needed = Math.max(Math.ceil(viewport.clientWidth / baseWidth) + LandingConstants.tickerOverflowRepeats, LandingConstants.tickerMinGroupRepeats);
    if (needed !== repeats) setRepeats(needed);
  }, [items, repeats]);

  // One marquee group: the base items repeated enough times to overflow the viewport.
  const groupItems = useMemo(() => Array.from({ length: repeats }, () => items).flat(), [items, repeats]);

  // No ticker-flagged niches → render only the empty panel (no scrolling track, no static fallback).
  if (items.length === 0) return <div className={Styles.ticker} />;

  // Two identical groups sit side by side; the track slides left by exactly one group (50%) so the
  // second copy lands where the first began — a continuous loop with no gap or snap-back.
  return (
    <div className={Styles.ticker} ref={viewportRef}>
      <div className={Styles.tickerTrack}>
        <div className={Styles.tickerGroup} ref={groupRef}>
          {groupItems.map((item, position) => (
            // Raw <span>: ticker item wrapper pairing the decorative dot with the item Label; engine Label renders text only.
            <span key={position} className={Styles.tickerItem}>
              {/* Raw <span>: empty decorative dot with no text; engine Label requires text and would add a title tooltip. */}
              <span className={Styles.dot} />
              <Label text={item} />
            </span>
          ))}
        </div>
        <div className={Styles.tickerGroup} aria-hidden="true">
          {groupItems.map((item, position) => (
            // Raw <span>: ticker item wrapper pairing the decorative dot with the item Label; engine Label renders text only.
            <span key={position} className={Styles.tickerItem}>
              {/* Raw <span>: empty decorative dot with no text; engine Label requires text and would add a title tooltip. */}
              <span className={Styles.dot} />
              <Label text={item} />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Card 1 — hero. Headline, value proposition, primary CTA, key stats, ticker.
 *
 * @param props - Component props.
 * @returns The hero card element.
 */
export function HeroCard(props: IHeroCardProps) {
  // Hero eyebrow words come from niches flagged for the hero; ticker items from niches flagged for the
  // ticker. Both arrive already translated from the catalog. With no ticker-flagged niches the strip
  // renders as an empty panel (see Ticker) rather than falling back to any static copy.
  const eyebrowWords = useMemo(() => props.niches.filter((entry) => entry.showInHero && entry.eyebrowLabel).map((entry) => entry.eyebrowLabel as string), [props.niches]);
  const tickerItems = useMemo(() => props.niches.filter((entry) => entry.showInTicker && entry.tickerLabel).map((entry) => entry.tickerLabel as string), [props.niches]);
  // Full headline text, shared as the title on every line Label so the multi-Label block reads as one phrase on hover.
  const heroHeadlineFull = `${Localized.LandingHeroHeadline1} ${Localized.LandingHeroHeadline2} ${Localized.LandingHeroHeadline3} ${Localized.LandingHeroHeadline4}`;
  // Full eyebrow tooltip: "AI Receptionist for: Roofing, Plumbing, HVAC, …"
  const eyebrowFull = `${Localized.LandingHeroEyebrowStatic}: ${eyebrowWords.join(', ')}`;
  // Read fresh each render so a language swap re-resolves the localized values (memoizing would freeze the previous language).
  const heroStats = ReadHeroStats();
  // Headline element, measured by the fit hook below.
  const headlineRef = useRef<HTMLHeadingElement>(null);

  // Fit-to-width: the headline lines are nowrap and splay left/right, so long languages (Greek, German) overflow the column and slide under the floating hex cluster. Measure each line plus its splay
  // against the column width and lower the `--hero-scale` variable until the whole block fits. Re-runs on resize, orientation change, and whenever the headline text changes (language/niche swap).
  useLayoutEffect(() => {
    const headline = headlineRef.current;
    if (!headline) return;
    const fit = () => {
      // Measure at natural size with wrapping OFF so the line widths and splay offsets are the true one-line values. The portrait wrap fallback only kicks in when even the floor scale overflows.
      headline.dataset.fitWrap = 'false';
      headline.style.setProperty('--hero-scale', '1');
      const available = headline.clientWidth;
      if (available <= 0) return;
      let maxNeeded = 0;
      for (const line of Array.from(headline.children) as HTMLElement[]) {
        const splay = Math.abs(Number.parseFloat(window.getComputedStyle(line).left)) || 0;
        // A line shifted by `splay` from centre needs its own width plus twice the shift to stay inside the column.
        maxNeeded = Math.max(maxNeeded, line.scrollWidth + 2 * splay);
      }
      if (maxNeeded <= 0) return;
      const usable = available * (1 - LandingConstants.heroHeadlineFitSlack);
      const rawScale = usable / maxNeeded;
      const scale = Math.max(LandingConstants.heroHeadlineMinScale, Math.min(1, rawScale));
      // When even the floor scale still cannot fit the longest line on one row, flag the wrap fallback (portrait CSS lets the lines wrap and drops the splay).
      headline.dataset.fitWrap = rawScale < LandingConstants.heroHeadlineMinScale ? 'true' : 'false';
      headline.style.setProperty('--hero-scale', `${scale}`);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(headline);
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fit);
      window.removeEventListener('orientationchange', fit);
    };
  }, [heroHeadlineFull]);

  return (
    <section className={Styles.hero}>
      <div className={Styles.heroMain}>
        <div className={Styles.heroColumn}>
          <div className={Styles.heroEyebrow}>
            {/* Raw <span>: inline wrapper grouping the static eyebrow Label with the word rotator; engine Label renders text only. */}
            <span className={Styles.eyebrowText}>
              <Label text={Localized.LandingHeroEyebrowStatic} classNameContainer={Styles.eyebrowStatic} title={eyebrowFull} />
              <EyebrowRotator words={eyebrowWords} title={eyebrowFull} />
            </span>
          </div>
          {/* Structural wrapper: the hero headline is a multi-line layout with a ref used for text fitting, so a div is used. Inner Labels carry the tooltip. */}
          <div className={Styles.heroHeadline} ref={headlineRef}>
            {/* Raw <span>: headline line wrapper holding two inner divs; engine Label renders text only. */}
            <span className={`${Styles.heroHeadlineLine} ${Styles.heroLineLeft}`}>
              <div className={Styles.heroLineLeftInner}>
                <Label text={Localized.LandingHeroHeadline1} title={heroHeadlineFull} classNameContainer={`${Styles.heroHeadlineLabel1}`} />
              </div>
              <div className={Styles.heroLineLeftInner}>
                <Label text={Localized.LandingHeroHeadline2} title={heroHeadlineFull} classNameContainer={Styles.accent} />
              </div>
            </span>
            {/* Raw <span>: headline line wrapper pairing two Labels inline; engine Label renders text only. */}
            <span className={`${Styles.heroHeadlineLine} ${Styles.heroHeadlineLabel3} ${Styles.heroLineRight}`}>
              <Label text={`${Localized.LandingHeroHeadline3} `} title={heroHeadlineFull} />
              <Label text={Localized.LandingHeroHeadline4} title={heroHeadlineFull} classNameContainer={Styles.accent2} />
            </span>
          </div>
          <div className={Styles.heroSub}>{FormatInlineBlocks(Localized.LandingHeroSub, [Styles.heroSubHeader, Styles.heroSubBold, Styles.heroSubAlt])}</div>
          <div className={Styles.heroActions}>
            <Button text={`${Localized.LandingHeroCTA} →`} classNameButtonEnabled={`${Styles.btnPrimary} ${Styles.ctaScrew}`} onClickHandler={props.onCalculate} />
          </div>
          <HexStatRotator stats={heroStats} />
        </div>
        <HexStatCluster stats={heroStats} />
      </div>
      <Ticker items={tickerItems} />
    </section>
  );
}
